import React, { useState, useEffect } from 'react';
import { 
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  CircularProgress,
  Tooltip,
  Chip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, People as PeopleIcon } from '@mui/icons-material';
import MainLayout from '../layouts/MainLayout';
import { toast } from 'react-toastify';
// import { usuarioService, Usuario, CreateUsuarioDTO } from '../services/usuarioService';
import { usuarioService ,Usuario,CreateUsuarioDTO} from '../services/usuarioService';
import { cargoService, Cargo } from '../services/cargoService';
import { permissaoService, Permissao } from '../services/permissaoService';

const formInicial: CreateUsuarioDTO = {
  nome: '',
  email: '',
  senha: '',
  ativo: true,
  isAdmin: false,
  cargoIds: [],
  permissaoIds: [],
};

const GerenciarAcessosPage = () => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [usuarioAtual, setUsuarioAtual] = useState<Partial<Usuario> | null>(null);
  const [formData, setFormData] = useState<CreateUsuarioDTO>(formInicial);
  const [cargos, setCargos] = useState<Cargo[]>([]);
  const [permissoes, setPermissoes] = useState<Permissao[]>([]);
  const [loadingCargos, setLoadingCargos] = useState(false);
  const [loadingPermissoes, setLoadingPermissoes] = useState(false);

  const carregarUsuarios = async () => {
    try {
      setLoading(true);
      const data = await usuarioService.listar();
      setUsuarios(data);
    } catch (error) {
      toast.error('Erro ao carregar usuários.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarUsuarios();
  }, []);

  const handleOpenDialog = async (usuario?: Usuario) => {
    setLoadingCargos(true);
    setLoadingPermissoes(true);
    try {
      const [cargosData, permissoesData] = await Promise.all([
        cargoService.listarCargos(),
        permissaoService.listarPermissoes()
      ]);
      setCargos(cargosData);
      setPermissoes(permissoesData);
    } catch (error) {
      toast.error('Erro ao carregar cargos ou permissões.');
    } finally {
      setLoadingCargos(false);
      setLoadingPermissoes(false);
    }

    setUsuarioAtual(usuario || null);
    setFormData(usuario ? { 
      nome: usuario.nome, 
      email: usuario.email, 
      ativo: usuario.ativo, 
      isAdmin: usuario.isAdmin, 
      cargoIds: usuario.cargos?.map(c => c.id) || [],
      permissaoIds: usuario.permissoes?.map(p => p.id) || []
    } : formInicial);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setUsuarioAtual(null);
  };

  const handleSave = async () => {
    if (!formData.nome || !formData.email) {
      toast.warn('Por favor, preencha nome e email.');
      return;
    }

    if (!usuarioAtual && !formData.senha) {
      toast.warn('A senha é obrigatória para novos usuários.');
      return;
    }

    try {
      if (usuarioAtual?.id) {
        const dadosParaAtualizar: Partial<CreateUsuarioDTO> = { ...formData };
        if (!formData.senha) {
          delete dadosParaAtualizar.senha; // Não envia a senha se estiver vazia
        }
        await usuarioService.atualizar(usuarioAtual.id, dadosParaAtualizar);
        toast.success('Usuário atualizado com sucesso!');
      } else {
        await usuarioService.criar(formData);
        toast.success('Usuário criado com sucesso!');
      }
      handleCloseDialog();
      carregarUsuarios();
    } catch (error) {
      toast.error('Erro ao salvar usuário.');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        await usuarioService.excluir(id);
        toast.success('Usuário excluído com sucesso!');
        carregarUsuarios();
      } catch (error) {
        toast.error('Erro ao excluir usuário.');
      }
    }
  };

  return (
    <MainLayout>
      <Box sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" color="primary.main" sx={{ fontWeight: 'bold' }}>
            <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2em' }} />
            Gerenciamento de Acessos
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Novo Usuário
          </Button>
        </Box>

        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', textTransform: 'uppercase', bgcolor: 'grey.100' } }}>
                  <TableCell>Nome</TableCell>
                  <TableCell>E-mail / Permissões</TableCell>
                  <TableCell>Cargo</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center"><CircularProgress /></TableCell>
                  </TableRow>
                ) : (
                  usuarios.map(usuario => (
                    <TableRow key={usuario.id} hover>
                      <TableCell>{usuario.nome}</TableCell>
                      <TableCell>
                        {usuario.email}
                        
                      </TableCell>
                      <TableCell>{usuario.cargos?.map(c => c.nome).join(', ') || (usuario.isAdmin ? 'Admin' : '-')}</TableCell>
                      <TableCell>
                        <Chip label={usuario.ativo ? 'Ativo' : 'Inativo'} color={usuario.ativo ? 'success' : 'error'} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton color="primary" onClick={() => handleOpenDialog(usuario)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir"> 
                          <IconButton color="error" onClick={() => handleDelete(usuario.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{usuarioAtual?.id ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
        <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Nome de Usuário"
                  fullWidth
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  helperText="Nome que será utilizado para o login no sistema."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="E-mail"
                  type="email"
                  fullWidth
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value.toLowerCase() })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Senha"
                  type="password"
                  fullWidth
                  value={formData.senha ?? ''}
                  onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                  helperText={usuarioAtual ? "Deixe em branco para não alterar" : "Senha é obrigatória"}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  multiple
                  options={cargos}
                  getOptionLabel={(option) => option.nome}
                  value={cargos.filter(c => formData.cargoIds?.includes(c.id))}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, cargoIds: newValue.map(c => c.id) });
                  }}
                  loading={loadingCargos}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cargos"
                      InputProps={{ ...params.InputProps, endAdornment: <>{loadingCargos ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</> }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={<Checkbox checked={formData.ativo} onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })} />}
                  label="Usuário Ativo"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={<Checkbox checked={formData.isAdmin} onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked })} />}
                  label="Administrador do Sistema"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  multiple
                  options={permissoes}
                  getOptionLabel={(option) => `${option.entidade.toUpperCase()}: ${option.descricao}`}
                  groupBy={(option) => option.entidade.toUpperCase()}
                  value={permissoes.filter(p => formData.permissaoIds?.includes(p.id))}
                  onChange={(_, newValue) => setFormData({ ...formData, permissaoIds: newValue.map(p => p.id) })}
                  loading={loadingPermissoes}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Permissões Individuais"
                      helperText="Estas permissões são adicionais às permissões herdadas dos cargos."
                      InputProps={{ ...params.InputProps, endAdornment: <>{loadingPermissoes ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>}}
                    />
                  )}
                />
              </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            {usuarioAtual ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};

export default GerenciarAcessosPage;